import { showEmeraldSelectionDialog } from 'src/common/components/interactive-dialogue/components/selection-dialogue/selection-dialog';
import { SelectListItem } from 'src/common/components/select-list/select-list';
import { IReasonCode } from 'src/common/interfaces/dtos/IReasonCode';
import { returnReasonCodeLookup } from 'src/return-services-display/business-logic/service/return-reason-code-lookup';
import { showIsPackageOpenModal } from 'src/returns/business-logic/handle-isPackageOpen-actions';
import { showSupervisorApprovalDialog } from '../../../brm/actions/supervisor-approval/supervisor-approval';
import { popEmeraldModal } from '../../emerald-modal-manager/emerald-modal-manager';
import * as EmeraldErrorCodes from '../../helpers/error-code';
import { ForcedExchangeIsNotCompleted } from '../../helpers/error-code';
import { ApprovalCredentialInput, IBRMMessage } from '../brm/brm-utils';
import { EmeraldError, EmeraldErrorSeverity } from '../emerald-error';
import { AbortedError, ProhibitActionError } from '../extended-emerald-error';

let modalId: string | undefined = undefined;
export interface IReturnPolicyInput {
  type: ReturnPolicyType;
  data?: string | ApprovalCredentialInput | IPackageOpenInput | IReasonCodeInput;
}

export enum ReturnPolicyType {
  ManagerApprovalRequired = 'ManagerApprovalRequered',
  IsPackageOpen = 'ReturnRule_IsOpenInputRequired',
  ProhibitAction = 'ReturnRule_DenyReturnedItem',
  ForcedExchangeIsNotCompleted = 'ForcedExchangeIsNotCompleted',
  MissingReasonOnReturnPolicy = 'MissingReasonOnReturnPolicy',
}

export interface IReturnPolicy {
  type: ReturnPolicyType;
  message?: IBRMMessage;
  data?: any;
}

export interface IPackageOpenInput {
  isPackageOpen?: string;
}

export interface IReasonCodeInput {
  reason?: string;
}

export const parseReturnPolicy = (error: EmeraldError): IReturnPolicy | undefined => {
  if (error.code === EmeraldErrorCodes.ReturnPolicy) {
    return error.data;
  }
};

export const dispatchReturnPolicy = async (
  policy: IReturnPolicy,
  depth: number,
): Promise<IReturnPolicyInput> => {
  return new Promise((resolve, reject) => {
    const input: IReturnPolicyInput = {
      type: policy.type,
    };
    const message = policy.message;
    const errorMessage = message ? message.message || message.caption : '';

    const onPackageOpen = (): void => {
      input.data = { isPackageOpen: 'True' };
      runApproval();
    };

    const onPackageClosed = (): void => {
      input.data = { isPackageOpen: 'False' };
      runApproval();
    };

    const onCancel = (): void => {
      reject(new AbortedError());
      popEmeraldModal(modalId);
      modalId = undefined;
    };

    const onApproval = (userName: string, password: string): void => {
      input.data = { userName, password };
      runApproval();
    };

    const onAcceptReasonCode = (data: string): void => {
      input.data = { reason: data };
      runApproval();
    };

    const onBarcodeScanned = async (barcode: string, password: string = ''): Promise<void> => {
      input.data = { barcode, password };
      runApproval();
    };

    const runApproval = (): void => {
      resolve(input);
      popEmeraldModal(modalId);
      modalId = undefined;
    };

    const selectReasonCode = (reasonCodes: IReasonCode[]) => {
      const selectListItems = reasonCodes.map(
        (reasonCode: IReasonCode): SelectListItem => ({
          id: reasonCode.value,
          description: reasonCode.description,
        }),
      );
      showEmeraldSelectionDialog({
        selectListItems,
        message: 'ReturnItemActionView_RefundReason',
        onCancel,
        onAccept: onAcceptReasonCode,
        dataTestId: 'reason-code-required',
      });
    };

    switch (policy.type) {
      case ReturnPolicyType.ManagerApprovalRequired: {
        modalId = 'Return_Policy_Approval_Modal';
        showSupervisorApprovalDialog(
          {
            errorMessage: errorMessage,
            onAccept: onApproval,
            onCancel,
            depth,
          },
          onBarcodeScanned,
          modalId,
        );
        break;
      }
      case ReturnPolicyType.IsPackageOpen: {
        showIsPackageOpenModal(onPackageOpen, onPackageClosed, onCancel);
        break;
      }
      case ReturnPolicyType.ProhibitAction: {
        throw new ProhibitActionError(errorMessage || 'Prohibit_Manager_Approval_text');
      }
      case ReturnPolicyType.ForcedExchangeIsNotCompleted: {
        throw new EmeraldError(
          ForcedExchangeIsNotCompleted,
          'Handle this outside return policy util',
          EmeraldErrorSeverity.Info,
          policy,
        );
      }
      case ReturnPolicyType.MissingReasonOnReturnPolicy: {
        const returnPolicyId =
          policy.data?.key === 'ReturnPolicyId' ? policy.data?.returnPolicyId : undefined;
        if (returnPolicyId) {
          returnReasonCodeLookup({ returnPolicyId })
            .then(selectReasonCode)
            .catch(reject);
        } else if (policy.data?.reasons) {
          selectReasonCode(policy.data.reasons);
        }
        break;
      }

      default: {
        throw new EmeraldError(
          'NotImplemented',
          `Return Policy ${policy.type} not implemented yet`,
        );
      }
    }
  });
};
